/**
 * ChoiceChips — a single-select row of neutral chips (the mock's `.optchip` set), used for the
 * relationship picker and the optional decline reasons. Accessible + never colour-alone: each chip
 * is a toggle button carrying `aria-pressed`, and the selected one shows a check glyph as well as
 * the coral fill, so the state reads without relying on colour. Chips meet the ≥44px target rule.
 */
import { cn } from '@/lib/utils';

export interface ChoiceOption<T extends string> {
  value: T;
  label: string;
}

interface ChoiceChipsProps<T extends string> {
  /** Accessible group name (rendered as the group's aria-label). */
  legend: string;
  options: ReadonlyArray<ChoiceOption<T>>;
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  /** Allow tapping the selected chip again to clear it (optional pickers, e.g. decline reason). */
  allowDeselect?: boolean;
  className?: string;
}

export function ChoiceChips<T extends string>({
  legend,
  options,
  value,
  onChange,
  allowDeselect = false,
  className,
}: ChoiceChipsProps<T>) {
  return (
    <div role="group" aria-label={legend} className={cn('flex flex-wrap gap-2', className)}>
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(selected && allowDeselect ? undefined : opt.value)}
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1.5 rounded-pill border px-4 font-display text-[13px] font-medium',
              'transition-colors duration-ui ease-thread',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
              selected
                // `ink`, not white: white on coral is 3.30:1 at this size. The hue is untouched —
                // the label gives way (4.92:1). Founder-ratified 16 Jul 2026; see 01-foundations.
                // The check glyph inherits via currentColor, so it follows the label.
                ? 'border-coral bg-coral text-ink'
                : 'border-line-2 bg-surface text-ink-70 hover:border-coral/40 hover:text-ink',
            )}
          >
            {selected && (
              <svg viewBox="0 0 24 24" fill="none" className="h-3.5 w-3.5" aria-hidden="true">
                <path
                  d="M5 13l4 4L19 7"
                  stroke="currentColor"
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
